import axiosInstance from "../../lib/axiosInstance";
import { toast } from "react-toastify";
import { AppDispatch } from "../store";
import { expenseActions } from "../slices/expense";
import { ExpenseMapper } from "../../mappers/ExpenseMapper";
import { getErrorMessage } from "../../utils/errorUtils";
import { ExpenseRequestDto } from "../../shared/dtos/expense/ExpenseRequestDto";
import { ExpenseResponseDto } from "../../shared/dtos/expense/ExpenseResponseDto";
import { ExpenseSummaryDto } from "../../shared/dtos/expense/ExpenseSummaryDto";
import { PaginationQueryDto } from "../../shared/dtos/common/PaginationQueryDto";
import { ApiResponseDto } from "../../shared/dtos/common/ApiResponseDto";
import { PaginatedResponse } from "../../shared/dtos/common/PaginatedResponse";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

export const getExpenses =
  (params?: PaginationQueryDto) => async (dispatch: AppDispatch) => {
    dispatch(expenseActions.setLoadingFetchAll());
    try {
      const { data } = await axiosInstance.get<
        ApiResponseDto<PaginatedResponse<ExpenseSummaryDto>>
      >("/expenses", {
        headers: { requiresAuth: true },
        params,
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        dispatch(expenseActions.setItems(data.data.content));
        dispatch(expenseActions.setPagination(data.data.pagination));
      }
    } catch (error) {
      dispatch(expenseActions.setErrorFetchAll(getErrorMessage(error)));
    }
  };

export const getExpenseById = (id: string) => async (dispatch: AppDispatch) => {
  dispatch(expenseActions.setLoadingFetchOne());
  try {
    const { data } = await axiosInstance.get<
      ApiResponseDto<ExpenseResponseDto>
    >(`/expenses/${id}`, {
      headers: { requiresAuth: true },
    });

    if (data.status === ApiResponseStatus.SUCCESS && data.data) {
      dispatch(expenseActions.setItem(data.data));
    }
  } catch (error) {
    dispatch(expenseActions.setErrorFetchOne(getErrorMessage(error)));
  }
};

export const createExpense =
  (payload: ExpenseRequestDto) => async (dispatch: AppDispatch) => {
    dispatch(expenseActions.setLoadingCreate());
    try {
      const { data } = await axiosInstance.post<
        ApiResponseDto<ExpenseResponseDto>
      >("/expenses", payload, {
        headers: { requiresAuth: true },
      });
      if (data.status === ApiResponseStatus.SUCCESS && data.data) {
        toast.success(data.message || "Expense recorded successfully!");
        const summary = ExpenseMapper.mapToSummary(data.data);
        dispatch(expenseActions.createSuccess(summary));
        dispatch(
          expenseActions.setServerStatus({
            operation: "create",
            status: data.statusCode || 201,
          }),
        );
      }
    } catch (error) {
      dispatch(expenseActions.setErrorCreate(getErrorMessage(error)));
    }
  };

export const voidExpense =
  (id: string, reason: string) => async (dispatch: AppDispatch) => {
    dispatch(expenseActions.setLoadingDelete()); // Map void to Delete loading state
    try {
      const { data } = await axiosInstance.post<ApiResponseDto<void>>(
        `/expenses/void/${id}?reason=${encodeURIComponent(reason)}`,
        null,
        { headers: { requiresAuth: true } },
      );
      if (data.status === ApiResponseStatus.SUCCESS) {
        toast.success("Expense voided!");
        dispatch(
          expenseActions.setServerStatus({ operation: "void", status: 200 }),
        );
      }
    } catch (error) {
      dispatch(expenseActions.setErrorDelete(getErrorMessage(error)));
    }
  };

export const resetExpenseServerStatus =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(expenseActions.setServerStatus(null));
  };
